import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import AdminLayout from '../components/AdminLayout';
import AdminBackButton from '../components/AdminBackButton';
import '../components/AdminLayout.css';
import ProjectSharingAdmin from '../components/admin/ProjectSharingAdmin';
import { STUDIO_VISUAL_QUALITY_GATE_HELP_HTML } from './studioVisualQualityGateHelp';

const API_BASE = process.env.REACT_APP_API_URL || 'http://localhost:5000';

type DefaultsTab = 'limits' | 'controls' | 'billing' | 'shorts' | 'fps' | 'social' | 'sharing';
const DEFAULT_TAB: DefaultsTab = 'limits';
const isDefaultsTab = (v: string | null): v is DefaultsTab => v === 'limits' || v === 'controls' || v === 'billing' || v === 'shorts' || v === 'fps' || v === 'social' || v === 'sharing';

// Shorts settings are grouped by which pipeline mode actually reads them — never one mixed
// list. There is no "default mode": a job always needs an explicit mode from the UI, the
// worker never picks one on its own (spec/shorts_settings.md §1).
type ShortsSubTab = 'general' | 'classic' | 'studio' | 'studio-local';
const DEFAULT_SHORTS_SUB_TAB: ShortsSubTab = 'general';
const isShortsSubTab = (v: string | null): v is ShortsSubTab => v === 'general' || v === 'classic' || v === 'studio' || v === 'studio-local';

interface AdminDefault {
  key: string;
  value: string;
  updatedAt: string;
}

interface ActivePlan {
  id: string;
  name: string;
}

interface FieldDef {
  key: string;
  label: string;
  hint: string;
}

const DEFAULT_FIELDS: Record<'limits' | 'controls' | 'billing' | 'fps', FieldDef[]> = {
  limits: [
    {
      key: 'limits.slideshow_max',
      label: 'Slideshow Max / Month',
      hint: 'Default monthly slideshow limit applied to users unless overridden.',
    },
    {
      key: 'limits.shorts_max',
      label: 'Shorts Max / Month',
      hint: 'Default monthly shorts generation limit.',
    },
    {
      key: 'limits.shorts_input_max_minutes',
      label: 'Shorts Input Max Minutes',
      hint: 'Default maximum input video duration allowed for shorts generation.',
    },
    {
      key: 'limits.shorts_output_max_minutes',
      label: 'Shorts Output Max Minutes',
      hint: 'Default maximum export duration allowed for a generated short.',
    },
    {
      key: 'limits.subtitle_max',
      label: 'Subtitle Max / Month',
      hint: 'Default monthly subtitle generation limit.',
    },
    {
      key: 'limits.youtube_posts_max',
      label: 'YouTube Posts Max / Month',
      hint: 'Default monthly posting cap for YouTube publishing.',
    },
    {
      key: 'limits.tiktok_posts_max',
      label: 'TikTok Posts Max / Month',
      hint: 'Default monthly posting cap for TikTok publishing.',
    },
  ],
  controls: [
    {
      key: 'controls.product_enabled',
      label: 'Default Product Access',
      hint: 'Default product access state for new users unless overridden.',
    },
    {
      key: 'controls.signup_requires_waitlist',
      label: 'Sign up: Waitlist Only',
      hint: 'When On, only emails with a subscribed waitlist entry can create a new account. Existing users can still sign in.',
    },
    {
      key: 'controls.subscription_plan_id',
      label: 'Default Subscription Plan',
      hint: 'Default plan assigned to new users. Use Pay As You Go to keep plan unassigned.',
    },
    {
      key: 'analytics.new_video_refresh_hrs',
      label: 'New Video Refresh (hrs)',
      hint: 'How often to refresh analytics for recent videos.',
    },
    {
      key: 'analytics.old_video_refresh_hrs',
      label: 'Old Video Refresh (hrs)',
      hint: 'How often to refresh analytics for older videos.',
    },
    {
      key: 'analytics.account_refresh_hrs',
      label: 'Account Refresh (hrs)',
      hint: 'How often to refresh account-level analytics.',
    },
  ],
  billing: [
    {
      key: 'billing.choose_plan_enabled',
      label: 'Show "Choose a plan" popup',
      hint: 'When On, signed-in users with no plan are shown the plan / pay-as-you-go popup after login. A plan picked on the Pricing page always opens it.',
    },
    {
      key: 'billing.choose_plan_reprompt_days',
      label: 'Re-prompt after dismiss (days)',
      hint: 'After a user closes the popup, wait this many days before showing it again. 0 shows it on every new browser session.',
    },
  ],
  fps: [
    {
      key: 'slideshow.fps',
      label: 'Slideshow FPS',
      hint: 'Frame rate the slideshow/story renderer composes and stitches at. Read by the worker '
        + 'at job start — no redeploy needed. Higher fps uses noticeably more memory while '
        + 'stitching. Falls back to 60 when unset, or 30 when the config API is unreachable.',
    },
  ],
};

const DEFAULT_TABS: Array<{ key: DefaultsTab; label: string }> = [
  { key: 'limits', label: 'Limits' },
  { key: 'controls', label: 'Controls' },
  { key: 'billing', label: 'Billing' },
  { key: 'shorts', label: 'Shorts' },
  { key: 'fps', label: 'FPS' },
  { key: 'social', label: 'Social Accounts' },
  { key: 'sharing', label: 'Project Sharing' },
];

// Social account linking limits — stored as one JSON blob under SOCIAL_CONFIG_KEY.
const SOCIAL_CONFIG_KEY = 'social_accounts_config';
const SOCIAL_PLATFORMS = [
  { id: 'youtube', label: 'YouTube' },
  { id: 'instagram', label: 'Instagram' },
  { id: 'facebook', label: 'Facebook' },
  { id: 'linkedin', label: 'LinkedIn' },
] as const;
type SocialPlatformId = typeof SOCIAL_PLATFORMS[number]['id'];

interface SocialPlatformConfig { allowed: boolean; perUser: number; tenantLinking: boolean }
interface SocialAccountsConfig {
  enabled: boolean;
  ownLimit: number;
  tenantLimit: number;
  inviteExpiryHours: number;
  platforms: Record<SocialPlatformId, SocialPlatformConfig>;
}

const DEFAULT_SOCIAL_CONFIG: SocialAccountsConfig = {
  enabled: false,
  ownLimit: 2,
  tenantLimit: 2,
  inviteExpiryHours: 24,
  platforms: {
    youtube: { allowed: true, perUser: 1, tenantLinking: false },
    instagram: { allowed: true, perUser: 1, tenantLinking: false },
    facebook: { allowed: true, perUser: 1, tenantLinking: false },
    linkedin: { allowed: true, perUser: 1, tenantLinking: false },
  },
};

const parseSocialConfig = (raw: string | undefined): SocialAccountsConfig => {
  try {
    const parsed = JSON.parse(raw || '');
    return {
      ...DEFAULT_SOCIAL_CONFIG,
      ...parsed,
      platforms: { ...DEFAULT_SOCIAL_CONFIG.platforms, ...(parsed.platforms || {}) },
    };
  } catch {
    return DEFAULT_SOCIAL_CONFIG;
  }
};

const FPS_OPTIONS = ['24', '30', '60'];

// spec/shorts_settings.md §2 — same three groups, same order, same field set.
const SHORTS_FIELDS: Record<ShortsSubTab, FieldDef[]> = {
  // Read by every mode — they all transcribe through the same stage.
  general: [
    {
      key: 'shorts.general.large_model',
      label: 'Large Whisper Model',
      hint: 'Which build runs when a job uses the Large whisper model, for Classic, Studio and '
        + 'Studio Local alike. Read by the worker at job start — no redeploy needed. Turbo is '
        + 'much faster at near large-v3 accuracy. A build not installed on the worker falls back '
        + 'to large-v3-int8. Falls back to WHISPER_LARGE_MODEL, then large-v3-int8.',
    },
    {
      key: 'shorts.active_speaker.model',
      label: 'Active Speaker Model',
      hint: 'Default signal for active-speaker reframing, for Classic, Studio and Manual alike. '
        + 'Legacy = mouth motion x audio loudness (current behaviour). Landmark and ASD are not '
        + 'implemented yet and run legacy. Read by the worker per job — no redeploy needed. '
        + 'Falls back to ACTIVE_SPEAKER_MODEL, then legacy.',
    },
  ],
  classic: [
    {
      key: 'shorts.classic.max_highlights',
      label: 'Classic Max Highlights',
      hint: 'Cap on highlights kept by the window-based analyze/segment/clip pipeline. Falls '
        + 'back to MAX_HIGHLIGHTS, then a code default that varies by call site (8 or 10 — a '
        + 'pre-existing inconsistency, not something this setting unifies).',
    },
    {
      key: 'shorts.classic.reframe_mode',
      label: 'Classic Reframe Mode',
      hint: 'Default crop strategy when a job doesn’t set its own (the per-job picker in '
        + 'Create Media always wins when set). Falls back to CLASSIC_REFRAME_MODE, then '
        + 'active-speaker.',
    },
  ],
  studio: [
    {
      key: 'shorts.studio.score_gate',
      label: 'Studio Score Gate',
      hint: 'Min score (0-100) for a candidate to be a clean accept rather than low-confidence. '
        + 'Read by the worker at job start — no redeploy needed. Falls back to STUDIO_SCORE_GATE, '
        + 'then 72.',
    },
    {
      key: 'shorts.studio.max_candidates',
      label: 'Studio Max Candidates',
      hint: 'Cap on generated candidates per video, before scoring/dedup. Falls back to '
        + 'STUDIO_MAX_CANDIDATES, then 40.',
    },
    {
      key: 'shorts.studio.max_shorts',
      label: 'Studio Max Shorts',
      hint: 'Cap on clean-accept shorts before the rest fall back to low-confidence. Falls back '
        + 'to STUDIO_MAX_SHORTS, then 8.',
    },
    {
      key: 'shorts.studio.visual_mode',
      label: 'Studio Visual Mode',
      hint: '"shadow" records freeze/blur/flicker checks without rejecting; "gate" acts on '
        + 'them; "off" skips the ffmpeg pass entirely. Falls back to STUDIO_VISUAL, then shadow.',
    },
    {
      key: 'shorts.studio.visual_expression',
      label: 'Studio Visual Expression',
      hint: 'Facial-expression signal method — never gates on its own. Falls back to '
        + 'STUDIO_VISUAL_EXPRESSION, then proxy.',
    },
    {
      key: 'shorts.studio.technical_analysis',
      label: 'Studio Technical Analysis',
      hint: 'Writes a diagnostic technical-analysis.json report (signals, hook detection, '
        + 'standalone-context checks) for the job. Does not render any clips on its own — see '
        + 'Save All Candidates below. Falls back to STUDIO_TECHNICAL_ANALYSIS, then off.',
    },
    {
      key: 'shorts.studio.dedup_enabled',
      label: 'Studio Dedup',
      hint: 'Clusters near-duplicate/overlapping candidates and keeps only the highest-scoring '
        + 'one per cluster. Off = every candidate survives to the quality gate. Falls back to '
        + 'STUDIO_DEDUP_ENABLED, then on.',
    },
    {
      key: 'shorts.studio.dedup_threshold',
      label: 'Studio Dedup Threshold',
      hint: 'Text-similarity score (0-1) above which two candidates count as near-duplicates. '
        + 'Only used when Studio Dedup is on. Falls back to STUDIO_DEDUP_THRESHOLD, then 0.55.',
    },
    {
      key: 'shorts.studio.save_all_candidates',
      label: 'Studio Save All Candidates',
      hint: 'Renders every rejected/dedup-dropped/paired-only candidate as an uploaded review '
        + 'clip (shown in the render list), independent of Technical Analysis above. Adds real '
        + 'render time per job. Falls back to STUDIO_SAVE_ALL_CANDIDATES, then off.',
    },
    {
      key: 'shorts.studio.reframe_mode',
      label: 'Studio Reframe Mode',
      hint: 'Default crop strategy when a job doesn’t set its own (the per-job picker in '
        + 'Create Media always wins when set). Falls back to STUDIO_REFRAME_MODE, then '
        + 'active-speaker.',
    },
  ],
  // Studio Local has no settings of its own — it's Studio with the two OpenAI calls replaced
  // by local heuristics (HeuristicScorer + TemplateMetadataWriter); every Studio setting above
  // still applies unchanged. Also not a selectable per-job mode today — still process-wide via
  // STUDIO_LOCAL in the worker's own .env, not resolved through this config API.
  'studio-local': [],
};

const SHORTS_SUB_TABS: Array<{ key: ShortsSubTab; label: string }> = [
  { key: 'general', label: 'General' },
  { key: 'classic', label: 'Classic' },
  { key: 'studio', label: 'Studio' },
  { key: 'studio-local', label: 'Studio Local' },
];

const LARGE_MODEL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'large-v3-int8', label: 'large-v3-int8 (quantized, low memory)' },
  { value: 'large-v3', label: 'large-v3 (full precision)' },
  { value: 'large-v3-turbo', label: 'large-v3-turbo (fastest)' },
];

const STUDIO_VISUAL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'shadow', label: 'Shadow (record only)' },
  { value: 'gate', label: 'Gate (reject on thresholds)' },
];

const STUDIO_VISUAL_EXPRESSION_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'off', label: 'Off' },
  { value: 'proxy', label: 'Proxy (facemark heuristic)' },
  { value: 'pyfeat', label: 'py-feat (full AU model)' },
];

// Shared by shorts.classic.reframe_mode and shorts.studio.reframe_mode — same three values
// either engine's crop logic accepts, see media-pipeline/src/studio/configDefaults.ts.
const REFRAME_MODE_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'active-speaker', label: 'Active speaker' },
  { value: 'fit-all-faces', label: 'Fit all faces' },
  { value: 'letterbox', label: 'Letterbox (no crop)' },
];

const ACTIVE_SPEAKER_MODEL_OPTIONS: Array<{ value: string; label: string }> = [
  { value: 'legacy', label: 'Legacy (mouth motion + audio)' },
  { value: 'landmark', label: 'Landmark mouth signal (not implemented yet)' },
  { value: 'asd', label: 'Audio-visual model (not implemented yet)' },
];

const CONTROL_DEFAULT_FALLBACKS: Record<string, string> = {
  'controls.product_enabled': 'true',
  'controls.subscription_plan_id': 'payg',
  'controls.signup_requires_waitlist': 'false',
  'billing.choose_plan_enabled': 'true',
  'billing.choose_plan_reprompt_days': '0',
  'slideshow.fps': '60',
  'shorts.studio.visual_mode': 'shadow',
  'shorts.studio.visual_expression': 'proxy',
  'shorts.studio.technical_analysis': 'false',
  'shorts.studio.dedup_enabled': 'true',
  'shorts.studio.dedup_threshold': '0.55',
  'shorts.studio.save_all_candidates': 'false',
  'shorts.classic.reframe_mode': 'active-speaker',
  'shorts.studio.reframe_mode': 'active-speaker',
  'shorts.general.large_model': 'large-v3-int8',
  'shorts.active_speaker.model': 'legacy',
};

// Same admin_defaults table, own dedicated UI elsewhere — AdminSpotWorkers.tsx's Settings tab
// (DEFAULT_DURATION_KEY) manages this one. Kept out of "Other Defaults" here so it isn't shown
// (and editable) in two places at once; still fully valid to save via /api/admin/defaults.
const MANAGED_ELSEWHERE_KEYS = ['spot_worker.default_protection_duration_min'];

const KNOWN_DEFAULT_KEYS = [
  ...Object.values(DEFAULT_FIELDS).flatMap((fields) => fields.map((field) => field.key)),
  ...Object.values(SHORTS_FIELDS).flatMap((fields) => fields.map((field) => field.key)),
  ...MANAGED_ELSEWHERE_KEYS,
  SOCIAL_CONFIG_KEY,
];

const AdminDefaults: React.FC = () => {
  const [defaults, setDefaults] = useState<AdminDefault[]>([]);
  const [activePlans, setActivePlans] = useState<ActivePlan[]>([]);
  const [loading, setLoading] = useState(true);
  const [edits, setEdits] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);
  const [showVisualGateHelp, setShowVisualGateHelp] = useState(false);
  const [pendingRoutes, setPendingRoutes] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  // New project routes without a member permission stay denied; surface that on every tab.
  useEffect(() => {
    fetch(`${API_BASE}/api/admin/sharing/routes`, { credentials: 'include' })
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (d) setPendingRoutes(d.pendingCount || 0); })
      .catch(() => { /* banner is optional */ });
  }, []);

  const tab: DefaultsTab = isDefaultsTab(searchParams.get('tab')) ? (searchParams.get('tab') as DefaultsTab) : DEFAULT_TAB;
  const setTab = (next: DefaultsTab) => setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    if (next === DEFAULT_TAB) params.delete('tab'); else params.set('tab', next);
    if (next !== 'shorts') params.delete('sub');
    return params;
  }, { replace: true });

  const shortsSubTab: ShortsSubTab = isShortsSubTab(searchParams.get('sub')) ? (searchParams.get('sub') as ShortsSubTab) : DEFAULT_SHORTS_SUB_TAB;
  const setShortsSubTab = (next: ShortsSubTab) => setSearchParams((prev) => {
    const params = new URLSearchParams(prev);
    if (next === DEFAULT_SHORTS_SUB_TAB) params.delete('sub'); else params.set('sub', next);
    return params;
  }, { replace: true });

  useEffect(() => {
    Promise.all([
      fetch(`${API_BASE}/api/admin/defaults`, { credentials: 'include' }).then((r) => r.json()),
      fetch(`${API_BASE}/api/admin/pricing/plans/active`, { credentials: 'include' }).then((r) => r.json()),
    ])
      .then(([defaultsData, plansData]) => {
        const rows: AdminDefault[] = defaultsData.defaults || [];
        setDefaults(rows);
        setActivePlans((plansData.plans || []).map((plan: { id: string; name: string }) => ({
          id: plan.id,
          name: plan.name,
        })));

        const initial: Record<string, string> = {};
        rows.forEach((d) => { initial[d.key] = d.value; });
        KNOWN_DEFAULT_KEYS.forEach((key) => {
          if (!initial[key]) initial[key] = '';
        });
        if (!initial[SOCIAL_CONFIG_KEY]) initial[SOCIAL_CONFIG_KEY] = JSON.stringify(DEFAULT_SOCIAL_CONFIG);
        Object.entries(CONTROL_DEFAULT_FALLBACKS).forEach(([key, value]) => {
          if (!initial[key]) initial[key] = value;
        });
        setEdits(initial);
      })
      .catch(() => setError('Failed to load defaults'))
      .finally(() => setLoading(false));
  }, []);

  const handleSaveAll = async () => {
    setSaving(true);
    setError(null);
    setSuccess(false);
    try {
      const entries = Object.entries(edits)
        .filter(([, value]) => value.trim() !== '')
        .map(([key, value]) => ({ key, value }));

      const res = await fetch(`${API_BASE}/api/admin/defaults`, {
        method: 'PUT',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: entries }),
      });

      if (!res.ok) {
        throw new Error('Save failed');
      }

      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch {
      setError('Failed to save defaults');
    } finally {
      setSaving(false);
    }
  };

  const socialConfig = parseSocialConfig(edits[SOCIAL_CONFIG_KEY]);
  const updateSocial = (patch: Partial<SocialAccountsConfig>) => setEdits((prev) => ({
    ...prev,
    [SOCIAL_CONFIG_KEY]: JSON.stringify({ ...parseSocialConfig(prev[SOCIAL_CONFIG_KEY]), ...patch }),
  }));
  const updateSocialPlatform = (id: SocialPlatformId, patch: Partial<SocialPlatformConfig>) => updateSocial({
    platforms: { ...socialConfig.platforms, [id]: { ...socialConfig.platforms[id], ...patch } },
  });

  const activeFields = tab === 'shorts' ? SHORTS_FIELDS[shortsSubTab] : (DEFAULT_FIELDS[tab as Exclude<DefaultsTab, 'shorts' | 'social' | 'sharing'>] ?? []);
  const otherDefaults = defaults.filter((d) => !KNOWN_DEFAULT_KEYS.includes(d.key));

  return (
    <AdminLayout>
      <div className="admin-page-header admin-page-header--back admin-page-header-row">
        <div>
          <div className="admin-page-title-row">
            <AdminBackButton />
            <h1 className="admin-page-title">Admin Defaults</h1>
          </div>
          <p className="admin-page-subtitle">Global system defaults applied to all users unless overridden.</p>
        </div>
        <button className="header-action-btn" disabled={saving} onClick={handleSaveAll}>
          {saving ? 'Saving…' : 'Save All'}
        </button>
      </div>

      {error && <div className="admin-error">{error}</div>}
      {pendingRoutes > 0 && tab !== 'sharing' && (
        <div className="admin-error" role="alert">
          {pendingRoutes} new route{pendingRoutes === 1 ? '' : 's'} need a project-sharing permission.{' '}
          <button type="button" className="admin-btn admin-btn-secondary admin-btn-compact" onClick={() => setTab('sharing')}>Review</button>
        </div>
      )}
      {success && <div className="admin-success">Saved successfully.</div>}

      {loading ? (
        <div className="admin-loading">Loading…</div>
      ) : (
        <>
          <div className="admin-tab-bar admin-tab-bar-spaced">
            {DEFAULT_TABS.map((tabItem) => (
              <button
                key={tabItem.key}
                type="button"
                onClick={() => setTab(tabItem.key)}
                className={`admin-tab-btn ${tab === tabItem.key ? 'active' : ''}`}
              >
                {tabItem.label}
              </button>
            ))}
          </div>

          {tab === 'shorts' && (
            <div className="admin-tab-bar admin-tab-bar-spaced">
              {SHORTS_SUB_TABS.map((subTabItem) => (
                <button
                  key={subTabItem.key}
                  type="button"
                  onClick={() => setShortsSubTab(subTabItem.key)}
                  className={`admin-tab-btn ${shortsSubTab === subTabItem.key ? 'active' : ''}`}
                >
                  {subTabItem.label}
                </button>
              ))}
            </div>
          )}

          {tab === 'sharing' && <ProjectSharingAdmin />}

          <div className="admin-card" style={tab === 'sharing' ? { display: 'none' } : undefined}>
            {tab === 'social' ? (
              <div className="admin-stack admin-stack-gap-20">
                <div>
                  <div className="admin-field-label-row">
                    <label className="admin-field-label admin-field-label-strong">Enable Tenant Account Linking</label>
                  </div>
                  <p className="admin-field-hint">
                    Master switch. When off, the limits below are disabled but their saved values are kept.
                  </p>
                  <select
                    className="admin-input admin-input-md"
                    value={socialConfig.enabled ? 'true' : 'false'}
                    onChange={(e) => updateSocial({ enabled: e.target.value === 'true' })}
                  >
                    <option value="false">Disabled</option>
                    <option value="true">Enabled</option>
                  </select>
                </div>
                <div>
                  <label className="admin-field-label admin-field-label-strong">Same User Own Account Linking Count</label>
                  <p className="admin-field-hint">Max accounts one user may link in total.</p>
                  <input
                    className="admin-input admin-input-md"
                    type="number"
                    min="0"
                    disabled={!socialConfig.enabled}
                    value={socialConfig.ownLimit}
                    onChange={(e) => updateSocial({ ownLimit: Math.max(0, Number(e.target.value) || 0) })}
                  />
                </div>
                <div>
                  <label className="admin-field-label admin-field-label-strong">Same User Tenant Account Linking Count</label>
                  <p className="admin-field-hint">Max channels one user may link that other users also hold.</p>
                  <input
                    className="admin-input admin-input-md"
                    type="number"
                    min="0"
                    disabled={!socialConfig.enabled}
                    value={socialConfig.tenantLimit}
                    onChange={(e) => updateSocial({ tenantLimit: Math.max(0, Number(e.target.value) || 0) })}
                  />
                </div>

                <div>
                  <label className="admin-field-label admin-field-label-strong">Tenant Invite Expiry (hours)</label>
                  <p className="admin-field-hint">How long a tenant invite link stays valid. Single use. 1–720.</p>
                  <input
                    className="admin-input admin-input-md"
                    type="number"
                    min="1"
                    max="720"
                    disabled={!socialConfig.enabled}
                    value={socialConfig.inviteExpiryHours}
                    onChange={(e) => updateSocial({ inviteExpiryHours: Math.min(720, Math.max(1, Number(e.target.value) || 1)) })}
                  />
                </div>

                {SOCIAL_PLATFORMS.map((platform) => {
                  const pc = socialConfig.platforms[platform.id];
                  return (
                    <React.Fragment key={platform.id}>
                      <hr className="admin-divider" />
                      <h3 className="admin-section-title">{platform.label}</h3>
                      <div>
                        <label className="admin-field-label admin-field-label-strong">Allow {platform.label} Platform</label>
                        <select
                          className="admin-input admin-input-md"
                          disabled={!socialConfig.enabled}
                          value={pc.allowed ? 'true' : 'false'}
                          onChange={(e) => updateSocialPlatform(platform.id, { allowed: e.target.value === 'true' })}
                        >
                          <option value="true">Allowed</option>
                          <option value="false">Not allowed</option>
                        </select>
                      </div>
                      <div>
                        <label className="admin-field-label admin-field-label-strong">
                          Same user, more than one {platform.label} account
                        </label>
                        <p className="admin-field-hint">Max {platform.label} accounts per user.</p>
                        <input
                          className="admin-input admin-input-md"
                          type="number"
                          min="0"
                          disabled={!socialConfig.enabled}
                          value={pc.perUser}
                          onChange={(e) => updateSocialPlatform(platform.id, { perUser: Math.max(0, Number(e.target.value) || 0) })}
                        />
                      </div>
                      <div>
                        <label className="admin-field-label admin-field-label-strong">Enable Tenant Account Linking</label>
                        <select
                          className="admin-input admin-input-md"
                          disabled={!socialConfig.enabled}
                          value={pc.tenantLinking ? 'true' : 'false'}
                          onChange={(e) => updateSocialPlatform(platform.id, { tenantLinking: e.target.value === 'true' })}
                        >
                          <option value="false">Off</option>
                          <option value="true">On</option>
                        </select>
                      </div>
                    </React.Fragment>
                  );
                })}
              </div>
            ) : tab === 'shorts' && shortsSubTab === 'studio-local' ? (
              <p className="admin-field-hint">
                Studio Local has no settings of its own — it's Studio with the two OpenAI calls
                (scoring, metadata) replaced by local heuristics. Every setting under the Studio
                tab still applies unchanged. It's also not a selectable per-job mode yet: it's
                toggled via <code>STUDIO_LOCAL</code> in the worker's own <code>.env</code>,
                process-wide, not per job — see spec/shorts_settings.md §2.
              </p>
            ) : (
              <div className="admin-stack admin-stack-gap-20">
                {activeFields.map((field) => (
                  <div key={field.key}>
                    <div className="admin-field-label-row">
                      <label className="admin-field-label admin-field-label-strong">{field.label}</label>
                      {field.key === 'shorts.studio.visual_mode' && (
                        <button
                          type="button"
                          className="admin-help-icon-btn"
                          aria-label="What does Studio Visual Mode do?"
                          onClick={() => setShowVisualGateHelp(true)}
                        >
                          i
                        </button>
                      )}
                    </div>
                    <p className="admin-field-hint">{field.hint}</p>
                    {field.key === 'controls.product_enabled' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'true'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        <option value="true">Enabled</option>
                        <option value="false">Disabled</option>
                      </select>
                    ) : field.key === 'controls.signup_requires_waitlist' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'false'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        <option value="false">Off</option>
                        <option value="true">On</option>
                      </select>
                    ) : field.key === 'billing.choose_plan_enabled' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'true'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        <option value="true">On</option>
                        <option value="false">Off</option>
                      </select>
                    ) : field.key === 'billing.choose_plan_reprompt_days' ? (
                      <input
                        className="admin-input admin-input-md"
                        type="number"
                        min="0"
                        step="1"
                        value={edits[field.key] ?? '0'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    ) : field.key === 'controls.subscription_plan_id' ? (
                      <select
                        className="admin-input admin-input-xl"
                        value={edits[field.key] ?? 'payg'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        <option value="payg">Pay As You Go</option>
                        {activePlans.map((plan) => (
                          <option key={plan.id} value={plan.id}>{plan.name}</option>
                        ))}
                      </select>
                    ) : field.key === 'slideshow.fps' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? '60'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {FPS_OPTIONS.map((fps) => (
                          <option key={fps} value={fps}>{fps} fps</option>
                        ))}
                      </select>
                    ) : field.key === 'shorts.studio.technical_analysis'
                      || field.key === 'shorts.studio.dedup_enabled'
                      || field.key === 'shorts.studio.save_all_candidates' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? (field.key === 'shorts.studio.dedup_enabled' ? 'true' : 'false')}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        <option value="false">Off</option>
                        <option value="true">On</option>
                      </select>
                    ) : field.key === 'shorts.studio.dedup_threshold' ? (
                      <input
                        className="admin-input admin-input-md"
                        type="number"
                        min="0"
                        max="1"
                        step="0.01"
                        value={edits[field.key] ?? ''}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    ) : field.key === 'shorts.studio.visual_mode' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'shadow'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {STUDIO_VISUAL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.key === 'shorts.studio.visual_expression' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'proxy'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {STUDIO_VISUAL_EXPRESSION_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.key === 'shorts.general.large_model' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'large-v3-int8'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {LARGE_MODEL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.key === 'shorts.active_speaker.model' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'legacy'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {ACTIVE_SPEAKER_MODEL_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : field.key === 'shorts.classic.reframe_mode' || field.key === 'shorts.studio.reframe_mode' ? (
                      <select
                        className="admin-input admin-input-md"
                        value={edits[field.key] ?? 'active-speaker'}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      >
                        {REFRAME_MODE_OPTIONS.map((opt) => (
                          <option key={opt.value} value={opt.value}>{opt.label}</option>
                        ))}
                      </select>
                    ) : (
                      <input
                        className="admin-input admin-input-md"
                        type="number"
                        min="0"
                        value={edits[field.key] ?? ''}
                        onChange={(e) => setEdits((prev) => ({ ...prev, [field.key]: e.target.value }))}
                      />
                    )}
                  </div>
                ))}
              </div>
            )}

            {otherDefaults.length > 0 && (
              <>
                <hr className="admin-divider" />
                <h3 className="admin-section-title">Other Defaults</h3>
                {otherDefaults.map((defaultItem) => (
                  <div key={defaultItem.key} className="admin-mb-12">
                    <label className="admin-key-label">{defaultItem.key}</label>
                    <input
                      className="admin-input admin-input-full admin-input-xl"
                      value={edits[defaultItem.key] ?? ''}
                      onChange={(e) => setEdits((prev) => ({ ...prev, [defaultItem.key]: e.target.value }))}
                    />
                  </div>
                ))}
              </>
            )}
          </div>
        </>
      )}

      {showVisualGateHelp && (
        <div className="admin-modal-overlay" onClick={(e) => e.target === e.currentTarget && setShowVisualGateHelp(false)}>
          <div className="admin-modal admin-visual-help-modal" role="dialog" aria-modal="true" aria-label="Studio Visual Quality Gate help">
            <div className="admin-asset-preview-header">
              <h2 className="admin-modal-title">Studio Visual Quality Gate</h2>
              <button
                type="button"
                className="admin-btn admin-btn-secondary admin-btn-compact"
                onClick={() => setShowVisualGateHelp(false)}
              >
                Close
              </button>
            </div>
            <iframe
              title="Studio Visual Quality Gate help"
              srcDoc={STUDIO_VISUAL_QUALITY_GATE_HELP_HTML}
              className="admin-visual-help-frame"
              sandbox="allow-same-origin"
            />
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminDefaults;
